<template>
    <div class="">
        <BlockUI v-if="loading" :message="loadingMsg">
            <b-icon icon="three-dots" animation="cylon" font-scale="4"></b-icon>
        </BlockUI>
        <div class="header header-layout-2 pos-static">
            <div class="header-layout-2-logoSection">
                <img class="header-logoIcon" src="../../assets/logoIcon.png" />
                <img class="header-logoImg" src="../../assets/loginPageLogo.png" />
            </div>
            <div>
                <p class="timerText">Time Left: {{ formattedTimer }}</p>
            </div>
        </div>
        <div class="assessment-page bg-primary-gradient-1 ">
            <div class="d-flex align-items-center justify-content-center text-white">

                <div class="container  py-5 px-3">
                    <!-- Progress -->
                    <div class="question-progress mb-4 px-3">
                        <div class="progress">
                            <div class="progress-bar bg-gradient-primary" role="progressbar"
                                :style="{ width: progressPercent + '%' }" :aria-valuenow="progressPercent"
                                aria-valuemin="0" aria-valuemax="100">
                                {{ progressPercent }}%
                            </div>
                        </div>
                    </div>



                    <!-- Question -->
                    <div class="text-center mb-5">
                        <h2 class="display-5 fw-bold mb-3">
                            {{ currentQuestion?.questionText }}
                        </h2>

                        <div v-if="currentQuestion?.images.length"
                            class="d-flex flex-wrap justify-content-center gap-3">
                            <img v-for="(img, i) in sortedImages" :key="i" :src="img.imageUrl" loading="lazy"
                                class="rounded shadow-lg question-img" alt="Question Image" />
                        </div>
                    </div>

                    <!-- Options -->
                    <div class="row g-3">
                        <div v-for="(option, index) in currentQuestion?.options" :key="index" class="col-md-6">
                            <div class="option-box p-4 rounded text-center fw-semibold"
                                :class="{ active: selectedOption === index }" @click="selectedOption = index">
                                {{ option.optionText }}
                            </div>
                        </div>
                    </div>

                    <!-- Error Message -->
                    <div v-if="showError" class="alert alert-danger mt-4 animated fadeIn">
                        Please select an option before proceeding.
                    </div>

                    <!-- Navigation -->
                    <div class="text-center mt-5">
                        <button class="btn btn-light text-primary px-5 py-2 fw-bold fs-5" @click="goNextQuestion">
                            {{ isLastQuestion ? 'Finish' : 'Next' }}
                        </button>
                    </div>
                </div>
            </div>

        </div>
        <b-modal id="submitted-info-modal" ref="error-modal" size="sm" no-close-on-backdrop hide-header-close
            buttonSize="sm" headerClass="p-2 border-bottom-0" footerClass="p-2 border-top-0" @ok="goBack" ok-only
            centered title="Information">
            <p class="my-4">You have submitted this problem.</p>
        </b-modal>
    </div>

</template>

<script>
import { getCandidateAssessmentDetails, updateCandidateRegistration, submitAssessment } from "../../api/index";
export default {
    data() {
        return this.initialState();
    },
    mounted() {
        this.getProblemData();

        // Event listeners
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
        window.addEventListener("resize", this.handleResize);

        // Listen to window focus/blur events to detect when the window gains or loses focus
        window.addEventListener("focus", this.handleFocus);
        window.addEventListener("blur", this.handleBlur);
    },
    beforeDestroy() {
        // Cleanup event listeners
        document.removeEventListener(
            "visibilitychange",
            this.handleVisibilityChange
        );
        window.removeEventListener("resize", this.handleResize);

        window.removeEventListener("focus", this.handleFocus);
        window.removeEventListener("blur", this.handleBlur);
    },
    computed: {
        currentQuestion() {
            return this.questions[this.currentQuestionIndex];
        },
        isLastQuestion() {
            return this.currentQuestionIndex === this.questions.length - 1;
        },
        progressPercent() {
            return Math.round((this.currentQuestionIndex / this.questions.length) * 100);
        },
        sortedImages() {
            return [...this.currentQuestion.images].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
        },
        showSubmit() {
            return this.problemData
                ? this.problemData.candidateProblemDetails?.Status != "Submitted"
                : false;
        },
        formattedTimer() {
            return this.$store.getters.formattedTimer;
        },
        remainingTime() {
            return this.$store.state.timer;
        },
    },
    watch: {
        // Watch the timer for changes
        async remainingTime(newValue) {
            if (newValue === 0) {
                this.timesupFlag = true;
                await this.submit();
                await this.completeInterview();
                this.navigateToTimeUp();
            }
        },
    },
    methods: {
        initialState() {
            return {
                loading: false,
                loadingMsg: "Please wait...",
                questions: [],
                currentQuestionIndex: 0,
                selectedOption: null,
                showError: false,
                answers: {}, // stores selected index per question
                problemData: [],
                timesupFlag: false,
                errMsg: "",

                isTabActive: true, // Track if the tab is active
                windowSizeChanged: false, // Track if the window size changes (minimizing or resizing)
                isWindowFocused: true, // Track if the window size has changed
                interviewConfig: this.$store.state.InterviewConfig,
                enableCheatingDetection: this.$store.state.InterviewConfig?.EnableCheatingDetection
            }
        },
        goBack() {
            this.$router.go(-1);
        },
        goNextQuestion() {
            if (this.selectedOption === null) {
                this.showError = true;
                setTimeout(() => {
                    this.showError = false;
                }, 3000);
                return;
            }

            // Save the selected answer
            this.answers[this.questions[this.currentQuestionIndex].questionId] = this.currentQuestion.options[this.selectedOption].optionId;

            // Reset for next question
            if (!this.isLastQuestion) {
                this.currentQuestionIndex++;
                this.selectedOption = null;
            } else {
                this.submit();
            }
        },
        getProblemData() {
            this.loading = true;
            let payload = {
                problem_Id: this.$route.params.Id,
                InterviewId: this.$store.state.InterviewId,
                CandidateId: this.$store.state.candidateInfo.Id,
                Type: this.$store.state.CandidateSelectedProblem.Type
            };
            getCandidateAssessmentDetails(payload)
                .then((resp) => {
                    this.problemData = resp.data.data;
                    this.questions = this.problemData.questions;
                    this.loading = false;
                    if (
                        this.problemData?.candidateProblemDetails?.Status == "Submitted"
                    ) {
                        this.$bvModal.show("submitted-info-modal");
                    }
                })
                .catch((err) => {
                    console.log("Error", err?.response?.data);
                    this.loading = false;
                });
        },

        async submit() {
            this.loading = true;
            const payload = {
                Answers: this.answers,
                problemId: this.$route.params.Id,
                InterviewId: this.$store.state.InterviewId,
                CandidateId: this.$store.state.candidateInfo.Id,
                CandidateProblemResId: this.$store.state.CandidateSelectedProblem.candidateResultId || 0,
            };
            await submitAssessment(payload)
                .then((resp) => {
                    console.log(resp);
                    this.loading = false;
                    if (!this.timesupFlag) { this.goBack(); }
                })
                .catch((err) => {
                    console.log("Error", err?.response?.data);
                    this.errMsg = err.response.data;
                    this.loading = false;
                });
        },
        async completeInterview() {
            const payload = {
                InterviewId: this.$store.state.InterviewId,
                CandidateId: this.$store.state.candidateInfo.Id,
                Status: "Completed"
            }
            await updateCandidateRegistration(payload)
                .then((resp) => {
                    if (resp) {
                        this.loading = false;
                    }
                })
                .catch((err) => {
                    console.log("Error", err);
                    this.errMsg = err.response.data.message;
                    this.loading = false;
                });
        },
        navigateToTimeUp() {
            this.$router.push("/timesupPage"); // Navigate to the Time-Up page
        },

        async handleVisibilityChange() {
            // The document.hidden property is true when the tab is not visible
            this.isTabActive = !document.hidden;
            console.log(this.isTabActive ? "Tab is active" : "Tab is inactive");

            if (this.enableCheatingDetection && !this.isTabActive && !this.loading) {
                await this.submit();
            }
        },
        async handleResize() {
            // Detect window resize event (this could be used to track minimization)
            this.windowSizeChanged = true;
            if (this.enableCheatingDetection && !this.loading) {
                await this.submit();
            }

            console.log("Window size changed");
            // setTimeout(() => {
            //   // Reset window size change after a brief delay to prevent continuous updates
            //   this.windowSizeChanged = false;
            // }, 500);
        },
        handleFocus() {
            // The window has gained focus (could be on any monitor)
            this.isWindowFocused = true;
            console.log("Window is focused");
        },
        async handleBlur() {
            // The window has lost focus (could be on any monitor)
            this.isWindowFocused = false;
            if (this.enableCheatingDetection && !this.loading) {
                await this.submit();
            }
            console.log("Window is not focused");
        },

    }
};
</script>

<style scoped>
.pos-static {
    position: static;
}

.assessment-page {
    min-height: 90vh;
    height: 100%;
}

.question-img {
    width: 300px;
    height: 200px;
    object-fit: contain;
    border: 3px solid #fff;
}

.option-box {
    background-color: rgba(255, 255, 255, 0.15);
    border: 2px solid transparent;
    transition: 0.3s ease;
    cursor: pointer;
    font-size: 1.25rem;
}

.option-box:hover {
    background-color: rgba(255, 255, 255, 0.25);
    transform: scale(1.02);
}

.option-box.active {
    background-color: #fff;
    color: #2575fc;
    border-color: #fff;
    transform: scale(1.05);
}

.alert-danger {
    background-color: #fdecea;
    color: #b71c1c;
    border: 1.5px solid #f5c6cb;
    font-weight: 600;
    text-align: center;
    border-radius: 6px;
    box-shadow: 0 4px 8px rgba(183, 28, 28, 0.15);
    padding: 12px 16px;
    max-width: 400px;
    margin: 0 auto;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animated.fadeIn {
    animation: fadeIn 0.3s ease-in-out;
}






.progress {
    height: 12px;
    border-radius: 10px;
    overflow: hidden;
    background-color: #e9ecef;
}

.progress-bar {
    background-image: linear-gradient(135deg, #5b86e5, #36d1dc);
    font-size: 12px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}
</style>